import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ROLES } from '@lms/shared-types';
import { PageLoader } from '@lms/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { contractSigningPath } from '@/lib/student-contract-gate';
import type { LegalPolicyConsentItem } from '@/features/legal-policies/types';

const ALLOWED_PREFIXES = [
  '/aceptar-politicas',
  '/mis-contratos',
  '/soporte',
  '/legal',
  '/profile',
  '/acceso-denegado',
  '/403',
];

function isLegalPolicyGateAllowedPath(pathname: string): boolean {
  return ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

interface PendingContractResponse {
  data: { id: string } | null;
}

export function LegalPolicyGuard() {
  const { pathname } = useLocation();
  const { hasRole } = usePermissions();
  const isStudent = hasRole(ROLES.STUDENT);

  const { data, isLoading: policiesLoading } = useQuery({
    queryKey: ['legal-policies', 'pending', 'blocksAccess'],
    queryFn: () =>
      api<{ data: LegalPolicyConsentItem[] }>('/legal/policies/pending?blocksAccess=true'),
    enabled: isStudent,
    staleTime: 0,
  });

  const { data: contractPending, isLoading: contractLoading } = useQuery({
    queryKey: ['student', 'contracts', 'pending'],
    queryFn: () => api<PendingContractResponse>('/student/contracts/pending'),
    enabled: isStudent,
    staleTime: 0,
  });

  if (!isStudent) {
    return <Outlet />;
  }

  if (policiesLoading || contractLoading) {
    return <PageLoader label="Verificando políticas legales…" />;
  }

  const pending = data?.data ?? [];
  if (pending.length > 0 && !isLegalPolicyGateAllowedPath(pathname)) {
    const contractId = contractPending?.data?.id;
    if (contractId) {
      return <Navigate to={contractSigningPath(contractId)} replace />;
    }
    return <Navigate to="/aceptar-politicas" replace />;
  }

  return <Outlet />;
}
